import { newLocalId } from '@/lib/protocol-ids';
import type { VariableDescriber, VariableStanding } from '@/lib/variable-pill';
import { objectiveFactsOf } from '@/lib/variable-pill';
import type {
  CriterionCard,
  CriterionRequirement,
  ExitAction,
  ExitActionKind,
  ExitCondition,
  ExitTrigger,
  ExperienceVariable,
  ObjectiveOutcome,
  ObjectiveQuantity,
  ProtocolObjectiveDraft,
  ProtocolStepDraft,
  ProtocolVariable,
  StepFallback,
  StepRules,
} from '@matter_extractor/shared';
import {
  DEFAULT_STEP_RULES,
  EMPTY_OBJECTIVE_QUANTITY,
  PROTOCOL_DEFAULT_CLOSE_TEXT,
  isTerminalAction,
  objectiveClassTriggerSchema,
} from '@matter_extractor/shared';

/**
 * Every gesture of the step editor, as a PURE function of what it changes
 * (refonte modulaire §7.2, §8.4, §8.6; maquette `editeur-protocole.html`).
 *
 * The panels of a step are five surfaces that all write into one
 * `ProtocolStepDraft`, and the page above them holds it: a component that
 * mutated a nested array in place would hand the page the object it already
 * had, and React would render nothing. So EVERY function here returns a new
 * object and none of them touches its argument - that is the whole contract,
 * and it is what the tests assert one gesture at a time.
 *
 * It lives in `lib/` rather than beside the components because a rule assembled
 * inside an `onClick` is a rule nothing can assert: `normalizeActions` and the
 * trigger parser are the two the mockup writes twice, and both are short enough
 * to be written differently in two places without anybody noticing.
 *
 * The rich field of §8.6 used to keep its two halves here - the DOM read back
 * as a token text, and a token text read as the pieces a renderer draws. Both
 * moved to `lib/variable-pill.ts` when the five text zones of the dispositif
 * met on ONE pill (grill Variables of 2026-09-16, D5, issue #88); what stays is
 * `protocolVariableDescriber`, which tells that module what a protocol's own
 * tokens are.
 */

/* ------------------------------------------------------------- the lookups */

/** One objective and where it lives - what a select and the palette both need. */
export type ObjectivePlacement = {
  readonly objective: ProtocolObjectiveDraft;
  readonly stepId: string;
  readonly stepIndex: number;
};

/** Every objective of the protocol, in reading order (§8.4, H20). */
export function allObjectives(steps: readonly ProtocolStepDraft[]): ObjectivePlacement[] {
  return steps.flatMap((step, stepIndex) =>
    step.objectives.map((objective) => ({ objective, stepId: step.id, stepIndex })),
  );
}

/**
 * What a trigger of THIS step may listen to: its own objectives and those of
 * the steps before it (§8.4).
 *
 * A later step's objective is not offered, and that is not a bound of input: at
 * the moment this step is played it has no status to change, so a condition on
 * it could never become true.
 */
export function availableTriggerObjectives(
  steps: readonly ProtocolStepDraft[],
  stepId: string,
): ObjectivePlacement[] {
  const here = steps.findIndex((step) => step.id === stepId);
  if (here < 0) return [];
  return allObjectives(steps.slice(0, here + 1));
}

/** Every step but this one - what a jump and a fallback may aim at. */
export function otherSteps(
  steps: readonly ProtocolStepDraft[],
  stepId: string,
): ProtocolStepDraft[] {
  return steps.filter((step) => step.id !== stepId);
}

/* ---------------------------------------------------------- the objectives */

/** A fresh objective: obligatoire by default (§7.2), named by its rank. */
export function newObjective(index: number): ProtocolObjectiveDraft {
  return {
    id: newLocalId(),
    name: `Objectif ${index + 1}`,
    mandatory: true,
    direction: '',
    // No level until somebody picks one (analyste.md §3.1, #196).
    analystLevel: null,
    criteria: [],
    quantity: EMPTY_OBJECTIVE_QUANTITY,
  };
}

export function replaceObjective(
  objectives: readonly ProtocolObjectiveDraft[],
  next: ProtocolObjectiveDraft,
): ProtocolObjectiveDraft[] {
  return objectives.map((one) => (one.id === next.id ? next : one));
}

export function removeObjective(
  objectives: readonly ProtocolObjectiveDraft[],
  objectiveId: string,
): ProtocolObjectiveDraft[] {
  return objectives.filter((one) => one.id !== objectiveId);
}

/**
 * The objective at `from` moves so that it SITS at `to` in the resulting list -
 * the same contract as the frise's `onMove`, and the reason it is written once:
 * « remove then insert » and « insert then remove » disagree by one whenever
 * the row travels downwards.
 */
export function moveObjective(
  objectives: readonly ProtocolObjectiveDraft[],
  from: number,
  to: number,
): ProtocolObjectiveDraft[] {
  const next = [...objectives];
  const [moved] = next.splice(from, 1);
  if (moved === undefined) return [...objectives];
  next.splice(Math.max(0, Math.min(to, next.length)), 0, moved);
  return next;
}

/* ------------------------------------------------------------ the criteria */

/** A criterion joins the condition de validation at `latest` (§4, vitrine.md V16). */
export function addCriterion(
  objective: ProtocolObjectiveDraft,
  criterionId: string,
): ProtocolObjectiveDraft {
  const requirement: CriterionRequirement = {
    ref: { id: criterionId, pin: 'latest' },
    threshold: null,
  };
  return { ...objective, criteria: [...objective.criteria, requirement] };
}

/**
 * One citation replaced in place, whole.
 *
 * ONE writer and not a setter per field: the row of §7.2 is a controlled
 * surface that hands back the citation it holds - version pill and threshold
 * together - so `setRequirementTag` and `setThreshold` had no caller but their
 * own tests, while `objective-row.tsx` re-spelled this same `map` by hand. Two
 * spellings of « remplacer le critère de rang N » is one too many.
 */
export function replaceRequirement(
  objective: ProtocolObjectiveDraft,
  index: number,
  requirement: CriterionRequirement,
): ProtocolObjectiveDraft {
  return {
    ...objective,
    criteria: objective.criteria.map((one, at) => (at === index ? requirement : one)),
  };
}

/**
 * A citation drawn out of its « l'un de » group: the key goes, rather than
 * being set to `undefined`, because the schema's `orGroup` is optional and
 * `exactOptionalPropertyTypes` refuses a present-but-undefined one.
 */
function withoutGroup(requirement: CriterionRequirement): CriterionRequirement {
  const { orGroup: _orGroup, ...rest } = requirement;
  return rest;
}

/**
 * The run of neighbouring citations sharing the label of the one at `index`, as
 * `[start, end)`. A citation with no label is a run of one - the same cut as
 * `objectiveCriterionGroups`, spelled by position because a gesture needs the
 * indices that the shared walk does not hand back.
 */
function groupRunAt(
  criteria: readonly CriterionRequirement[],
  index: number,
): readonly [start: number, end: number] {
  const label = criteria[index]?.orGroup;
  if (label === undefined) return [index, index + 1];
  let start = index;
  while (start > 0 && criteria[start - 1]?.orGroup === label) start -= 1;
  let end = index + 1;
  while (end < criteria.length && criteria[end]?.orGroup === label) end += 1;
  return [start, end];
}

/** A label no citation of the objective carries yet: short, and unique where it is read. */
function freshOrGroup(criteria: readonly CriterionRequirement[]): string {
  const taken = new Set<string>();
  for (const one of criteria) if (one.orGroup !== undefined) taken.add(one.orGroup);
  let n = 1;
  while (taken.has(`g${n}`)) n += 1;
  return `g${n}`;
}

/**
 * A criterion leaves the condition de validation.
 *
 * It also settles the group it leaves (issue #181): a « l'un de » that falls to
 * a single citation loses its label, so the cross of a two-member group hands
 * back a plain required line instead of a group the version would refuse. Only
 * THAT group is settled - a lone labelled citation that was already there is a
 * defect the editor must keep showing, not one a gesture elsewhere may erase.
 */
export function removeCriterion(
  objective: ProtocolObjectiveDraft,
  index: number,
): ProtocolObjectiveDraft {
  const removed = objective.criteria[index];
  const kept = objective.criteria.filter((_one, at) => at !== index);
  const label = removed?.orGroup;
  if (label === undefined) return { ...objective, criteria: kept };
  const members = kept.filter((one) => one.orGroup === label);
  if (members.length !== 1) return { ...objective, criteria: kept };
  return {
    ...objective,
    criteria: kept.map((one) => (one.orGroup === label ? withoutGroup(one) : one)),
  };
}

/**
 * « + ou » between the citation at `index` and the one below it: the two become
 * one « l'un de » group (issue #181, scenario-succes-conduite.md §7).
 *
 * If one of them is already in a group the other JOINS it - the whole group
 * below is taken in when it is the lower one that has a group - and otherwise a
 * fresh label is made. Two citations already of one group stay as they are.
 */
export function linkCriteria(
  objective: ProtocolObjectiveDraft,
  index: number,
): ProtocolObjectiveDraft {
  const upper = objective.criteria[index];
  const lower = objective.criteria[index + 1];
  if (upper === undefined || lower === undefined) return objective;
  if (upper.orGroup !== undefined && upper.orGroup === lower.orGroup) return objective;
  const label = upper.orGroup ?? lower.orGroup ?? freshOrGroup(objective.criteria);
  const [from] = groupRunAt(objective.criteria, index);
  const [, to] = groupRunAt(objective.criteria, index + 1);
  return {
    ...objective,
    criteria: objective.criteria.map((one, at) =>
      at >= from && at < to ? { ...one, orGroup: label } : one,
    ),
  };
}

/**
 * « ou × » above the citation at `index`: its group is cut right there, the line
 * below and what follows it leaving the group (issue #181).
 *
 * A side left with a single citation becomes a plain required line, so a group
 * of two disappears and a group of three leaves two lines or a group and a line.
 * The lower side keeps a label of its own, fresh, rather than the upper's: two
 * runs sharing one label would be a refusal (`orGroupProblems`).
 *
 * The first member of a group has no joint above it. On a group of ONE citation
 * - the refused state - the same gesture drops the label, which is what
 * « Délier » says.
 */
export function unlinkCriterion(
  objective: ProtocolObjectiveDraft,
  index: number,
): ProtocolObjectiveDraft {
  const target = objective.criteria[index];
  if (target?.orGroup === undefined) return objective;
  const [start, end] = groupRunAt(objective.criteria, index);
  if (end - start === 1) {
    return {
      ...objective,
      criteria: objective.criteria.map((one, at) => (at === index ? withoutGroup(one) : one)),
    };
  }
  if (index === start) return objective;
  const keepsUpper = index - start >= 2;
  const lowerLabel = end - index >= 2 ? freshOrGroup(objective.criteria) : null;
  return {
    ...objective,
    criteria: objective.criteria.map((one, at) => {
      if (at < start || at >= end) return one;
      if (at < index) return keepsUpper ? one : withoutGroup(one);
      return lowerLabel === null ? withoutGroup(one) : { ...one, orGroup: lowerLabel };
    }),
  };
}

/**
 * « Ajouter au groupe »: a criterion joins the condition de validation as one
 * more member of the group `orGroup`, right under its last member so the group
 * stays made of neighbours. Without such a group it lands at the end, plain.
 */
export function addCriterionToGroup(
  objective: ProtocolObjectiveDraft,
  criterionId: string,
  orGroup: string,
): ProtocolObjectiveDraft {
  const first = objective.criteria.findIndex((one) => one.orGroup === orGroup);
  if (first === -1) return addCriterion(objective, criterionId);
  const [, end] = groupRunAt(objective.criteria, first);
  const requirement: CriterionRequirement = {
    ref: { id: criterionId, pin: 'latest' },
    threshold: null,
    orGroup,
  };
  const criteria = [...objective.criteria];
  criteria.splice(end, 0, requirement);
  return { ...objective, criteria };
}

export function setQuantity(
  objective: ProtocolObjectiveDraft,
  quantity: ObjectiveQuantity,
): ProtocolObjectiveDraft {
  return { ...objective, quantity };
}

/* --------------------------------------------------------------- the rules */

/**
 * A number field read back from an `<input type="number">`.
 *
 * Empty means « not posed » wherever `null` is a value the schema holds
 * (`maxTurns`), and the floor everywhere else: a browser lets a number field be
 * emptied whatever its `min`, and a `NaN` written into the draft would travel
 * all the way to a 400.
 */
function readNumber(raw: string, floor: number): number | null {
  if (raw.trim() === '') return null;
  const value = Number(raw);
  if (!Number.isFinite(value)) return null;
  return Math.max(floor, Math.trunc(value));
}

/** « plafond de tours » - required for a version, nullable in a draft (§7.1). */
export function setMaxTurns(rules: StepRules, raw: string): StepRules {
  return { ...rules, maxTurns: readNumber(raw, 1) };
}

export function setEmptyAnswers(rules: StepRules, raw: string): StepRules {
  return { ...rules, emptyAnswersAllowed: readNumber(raw, 0) ?? 0 };
}

export function setAttempts(rules: StepRules, raw: string): StepRules {
  return { ...rules, attempts: readNumber(raw, 1) ?? 1 };
}

/**
 * The repli beyond the attempts (§7.2).
 *
 * Choosing « aller à une étape » lands on the FIRST other step rather than on
 * nothing: a select with no value would write a jump to `''`, which the frise
 * cannot draw and which `protocolProblems` reports as a dangling target - a
 * refusal produced by the act of choosing.
 *
 * Coming back to « fermer l'expérience » is a no-op when it is already the
 * fallback, so the closing text somebody typed is never reset by a second click
 * on the tile that is already checked.
 */
export function setFallback(
  rules: StepRules,
  kind: StepFallback['kind'],
  otherStepIds: readonly string[],
): StepRules {
  if (kind === 'go-to-step') {
    if (rules.fallback.kind === 'go-to-step') return rules;
    return { ...rules, fallback: { kind: 'go-to-step', stepId: otherStepIds[0] ?? '' } };
  }
  if (rules.fallback.kind === 'close-experience') return rules;
  return { ...rules, fallback: { kind: 'close-experience', text: PROTOCOL_DEFAULT_CLOSE_TEXT } };
}

export function setFallbackText(rules: StepRules, text: string): StepRules {
  if (rules.fallback.kind !== 'close-experience') return rules;
  return { ...rules, fallback: { kind: 'close-experience', text } };
}

export function setFallbackTarget(rules: StepRules, stepId: string): StepRules {
  if (rules.fallback.kind !== 'go-to-step') return rules;
  return { ...rules, fallback: { kind: 'go-to-step', stepId } };
}

/* ------------------------------------------------------ the farewell step */

/**
 * The checkbox « Étape d'adieu » (issue #163, §7.2), ticked or unticked.
 *
 * The RULES go back to `DEFAULT_STEP_RULES` both ways, and ticking also unticks
 * the reread on entry (issue #165) - that is the whole of what this does beyond
 * the flag. Ticking: a farewell step keeps « des `rules`
 * par défaut que le moteur ignore » (H76), and a repli left on it would still
 * be read by `protocolJumps` - the frise would draw an exit from a step that
 * has none. Unticking: « décocher rend les trois blocs, vides, avec les règles
 * par défaut » (§7.2), the blocks of a step that was never anything else.
 *
 * The objectives and conditions are left as they are. The box only ticks on a
 * step that has none (`farewellEligible`), so on the way in there is nothing to
 * lose; on the way out, a farewell step posed by hand WITH objectives
 * (`farewell-step-not-empty`) gets them back in view rather than erased.
 */
export function setFarewell(step: ProtocolStepDraft, farewell: boolean): ProtocolStepDraft {
  return {
    ...step,
    farewell,
    // Ticking the farewell UNTICKS the reread on entry (issue #165, H93): a
    // farewell step has no objectives to fill, and the pair is refused as
    // `reread-on-farewell-step`. Unticking leaves it off - nothing to restore.
    rereadOnEntry: farewell ? false : step.rereadOnEntry,
    // Same for « Cacher les objectifs à l'interviewer » (issue #191, H19): a
    // farewell step has no objectives to hide (`hide-objectives-on-farewell-step`).
    hideObjectives: farewell ? false : step.hideObjectives,
    rules: DEFAULT_STEP_RULES,
  };
}

/* ----------------------------------------------------- the exit conditions */

/** The condition « Ajouter une condition de sortie » creates (maquette). */
export function newCondition(): ExitCondition {
  return {
    id: newLocalId(),
    groups: [[{ kind: 'objective-class', which: 'all-mandatory-succeeded' }]],
    actions: [{ kind: 'close-step' }],
  };
}

/** The trigger a « + et » starts from. */
function newTrigger(): ExitTrigger {
  return { kind: 'objective-class', which: 'all-mandatory-succeeded' };
}

export function addTrigger(condition: ExitCondition, groupIndex: number): ExitCondition {
  return {
    ...condition,
    groups: condition.groups.map((group, at) =>
      at === groupIndex ? [...group, newTrigger()] : group,
    ),
  };
}

/**
 * A trigger leaves; an emptied group leaves with it; and a condition never ends
 * up with nothing to listen to.
 *
 * `exitConditionSchema` asks for at least one group of at least one trigger, so
 * emptying the last one would make the draft unparseable on its way back from
 * the server - the mockup rebuilds a default trigger for exactly this reason.
 */
export function removeTrigger(
  condition: ExitCondition,
  groupIndex: number,
  triggerIndex: number,
): ExitCondition {
  const groups = condition.groups
    .map((group, at) => (at === groupIndex ? group.filter((_one, i) => i !== triggerIndex) : group))
    .filter((group) => group.length > 0);
  return { ...condition, groups: groups.length > 0 ? groups : [[newTrigger()]] };
}

export function addGroup(condition: ExitCondition): ExitCondition {
  return { ...condition, groups: [...condition.groups, [{ kind: 'turn-cap-reached' }]] };
}

/**
 * The value of the trigger select, read back: `obj:<id>`, `type:<which>`, or
 * one of the three counter kinds.
 *
 * The outcome of an objective trigger SURVIVES a change from one objective to
 * another - « devient raté » is a choice about the condition, not about which
 * objective it names, and losing it on every change of the first select is the
 * kind of erasure nobody notices until a protocol behaves backwards.
 */
export function parseTriggerValue(value: string, previous: ExitTrigger): ExitTrigger {
  if (value.startsWith('obj:')) {
    return {
      kind: 'objective-status',
      objectiveId: value.slice(4),
      to: previous.kind === 'objective-status' ? previous.to : 'succeeded',
    };
  }
  if (value.startsWith('type:')) {
    const which = objectiveClassTriggerSchema.safeParse(value.slice(5));
    return {
      kind: 'objective-class',
      which: which.success ? which.data : 'all-mandatory-succeeded',
    };
  }
  if (
    value === 'turn-cap-reached' ||
    value === 'attempts-exhausted' ||
    value === 'empty-answers-exceeded'
  ) {
    return { kind: value };
  }
  return previous;
}

/** What the trigger select shows as selected, for the trigger it is drawn from. */
export function triggerValue(trigger: ExitTrigger): string {
  if (trigger.kind === 'objective-status') return `obj:${trigger.objectiveId}`;
  if (trigger.kind === 'objective-class') return `type:${trigger.which}`;
  return trigger.kind;
}

function mapTrigger(
  condition: ExitCondition,
  groupIndex: number,
  triggerIndex: number,
  change: (trigger: ExitTrigger) => ExitTrigger,
): ExitCondition {
  return {
    ...condition,
    groups: condition.groups.map((group, at) =>
      at === groupIndex ? group.map((one, i) => (i === triggerIndex ? change(one) : one)) : group,
    ),
  };
}

export function setTrigger(
  condition: ExitCondition,
  groupIndex: number,
  triggerIndex: number,
  value: string,
): ExitCondition {
  return mapTrigger(condition, groupIndex, triggerIndex, (one) => parseTriggerValue(value, one));
}

export function setTriggerOutcome(
  condition: ExitCondition,
  groupIndex: number,
  triggerIndex: number,
  to: ObjectiveOutcome,
): ExitCondition {
  return mapTrigger(condition, groupIndex, triggerIndex, (one) =>
    one.kind === 'objective-status' ? { ...one, to } : one,
  );
}

/* ------------------------------------------------------------- the actions */

/** What a fresh action needs to aim at something rather than at nothing. */
export type ActionContext = {
  /** Every step but the one being edited, in order. */
  readonly otherStepIds: readonly string[];
  /** Every objective of the PROTOCOL: an invalidation may reach back (H20). */
  readonly objectiveIds: readonly string[];
};

export function newAction(kind: ExitActionKind, context: ActionContext): ExitAction {
  switch (kind) {
    case 'direction':
      return { kind: 'direction', text: '' };
    case 'invalidate-objective':
      return { kind: 'invalidate-objective', objectiveId: context.objectiveIds[0] ?? '' };
    case 'go-to-step':
      return { kind: 'go-to-step', stepId: context.otherStepIds[0] ?? '' };
    case 'close-experience':
      return { kind: 'close-experience', text: PROTOCOL_DEFAULT_CLOSE_TEXT };
    default:
      return { kind };
  }
}

/**
 * The shape §8.4 fixes: the non-terminal actions in order, then AT MOST ONE
 * terminal, last.
 *
 * The LAST terminal wins rather than the first, and that is the mockup's rule
 * for a reason an operator can feel: changing an action's kind to a second
 * terminal one is a statement about what should now end the condition, so it is
 * the older one that goes.
 */
export function normalizeActions(actions: readonly ExitAction[]): ExitAction[] {
  const kept = actions.filter((action) => !isTerminalAction(action));
  const terminals = actions.filter((action) => isTerminalAction(action));
  const last = terminals[terminals.length - 1];
  return last === undefined ? kept : [...kept, last];
}

/**
 * « + action » inserts a `direction` BEFORE the terminal one, never after it -
 * which is what the label says when a terminal action is already there
 * (« action non terminale, avant la dernière »).
 */
export function addAction(condition: ExitCondition, context: ActionContext): ExitCondition {
  const fresh = newAction('direction', context);
  const kept = condition.actions.filter((action) => !isTerminalAction(action));
  const terminals = condition.actions.filter((action) => isTerminalAction(action));
  return { ...condition, actions: normalizeActions([...kept, fresh, ...terminals]) };
}

/**
 * Changing an action's kind REPLACES it rather than patching it: the seven
 * kinds are a discriminated union, and « aller à une étape » carries a target
 * that « clôturer l'étape » has no field for.
 */
export function setActionKind(
  condition: ExitCondition,
  index: number,
  kind: ExitActionKind,
  context: ActionContext,
): ExitCondition {
  const actions = condition.actions.map((one, at) =>
    at === index ? newAction(kind, context) : one,
  );
  return { ...condition, actions: normalizeActions(actions) };
}

/** The one parameter an action carries, named by the field it writes into. */
export type ActionParam =
  | { readonly field: 'text'; readonly value: string }
  | { readonly field: 'stepId'; readonly value: string }
  | { readonly field: 'objectiveId'; readonly value: string };

export function setActionParam(
  condition: ExitCondition,
  index: number,
  param: ActionParam,
): ExitCondition {
  return {
    ...condition,
    actions: condition.actions.map((one, at) => {
      if (at !== index) return one;
      if (param.field === 'text') {
        if (one.kind === 'direction' || one.kind === 'close-experience') {
          return { ...one, text: param.value };
        }
        return one;
      }
      if (param.field === 'stepId') {
        return one.kind === 'go-to-step' ? { ...one, stepId: param.value } : one;
      }
      return one.kind === 'invalidate-objective' ? { ...one, objectiveId: param.value } : one;
    }),
  };
}

export function removeAction(condition: ExitCondition, index: number): ExitCondition {
  return { ...condition, actions: condition.actions.filter((_one, at) => at !== index) };
}

/* ------------------------------------------------------ the condition list */

export function replaceCondition(
  conditions: readonly ExitCondition[],
  next: ExitCondition,
): ExitCondition[] {
  return conditions.map((one) => (one.id === next.id ? next : one));
}

export function removeCondition(
  conditions: readonly ExitCondition[],
  conditionId: string,
): ExitCondition[] {
  return conditions.filter((one) => one.id !== conditionId);
}

/** The order of the conditions IS their priority (§8.4), so it is data. */
export function moveCondition(
  conditions: readonly ExitCondition[],
  from: number,
  to: number,
): ExitCondition[] {
  const next = [...conditions];
  const [moved] = next.splice(from, 1);
  if (moved === undefined) return [...conditions];
  next.splice(Math.max(0, Math.min(to, next.length)), 0, moved);
  return next;
}

/* --------------------------------------------------------- the rich field */

/**
 * What each token of a direction IS, for the pill module (§8.6; grill Variables
 * of 2026-09-16, D5).
 *
 * The protocol editor was the first zone to draw a pill, and it now draws the
 * same one as the four zones of an experience: this is its describer, built
 * from the steps being edited and the criteria library. §8.6 stores the ID and
 * displays the NAME, so this lookup is what makes a rename invisible to a
 * direction. A RETIRED criterion still resolves - the pill keeps naming it, and
 * the refusal it will cause at « Passer en production » is `productionBlockers`'s
 * to say, not the pill's. A field or a choice - which a protocol never stores -
 * answers no facts, so the pill would name it unknown by its kind.
 *
 * Nothing here is pending and nothing is out of reach: the steps ARE the
 * protocol, read by the editor holding them, and a direction has no page to be
 * served on.
 */
export function protocolVariableDescriber(
  steps: readonly ProtocolStepDraft[],
  criteria: readonly CriterionCard[],
): VariableDescriber {
  const cards = new Map<string, string>();
  for (const card of criteria) cards.set(card.id, card.name);
  return (variable: ExperienceVariable): VariableStanding => {
    let facts: VariableStanding['facts'] = null;
    if (variable.kind === 'objective') facts = objectiveFactsOf(steps, variable.id, null);
    if (variable.kind === 'criterion') {
      const name = cards.get(variable.id);
      facts = name === undefined ? null : { kind: 'criterion', name };
    }
    return { variable, facts, pending: false, breach: null };
  };
}

/* ------------------------------------------------------------- the palette */

/** One line of the palette: a chip to insert and what tells it from a homonym. */
export type PaletteItem = {
  readonly variable: ProtocolVariable;
  readonly label: string;
  readonly meta: string;
  /** Two objectives share this name: the meta carries the step and is inked. */
  readonly duplicate: boolean;
};

export type PaletteGroup = {
  /**
   * What tells the group apart when two titles may be equal - the step's id
   * in the palette of the reading (#158), whose titles are step names. Absent,
   * the title is the group's identity: the three of a direction are fixed.
   */
  readonly id?: string;
  readonly title: string;
  readonly items: readonly PaletteItem[];
};

/** At most eight criteria, so the palette stays a list and not a library. */
const PALETTE_CRITERIA_MAX = 8;

/**
 * The three groups of §7.2: the objectives of this step, those of the previous
 * ones, and the criteria - « catégories alignées à gauche et variables
 * décalées, un homonyme accompagné de son étape et de son obligation ».
 *
 * Retired criteria are left out: a direction that cited one would be refused at
 * « Passer en production », and offering it here would be offering a refusal.
 */
export function paletteGroups(
  steps: readonly ProtocolStepDraft[],
  stepId: string,
  criteria: readonly CriterionCard[],
  query: string,
): PaletteGroup[] {
  const needle = query.trim().toLowerCase();
  const matches = (name: string): boolean => needle === '' || name.toLowerCase().includes(needle);

  const reachable = availableTriggerObjectives(steps, stepId);
  const counts = new Map<string, number>();
  for (const place of reachable) {
    const key = place.objective.name.trim().toLowerCase();
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  const duplicated = (name: string): boolean => (counts.get(name.trim().toLowerCase()) ?? 0) > 1;

  const here = steps.findIndex((step) => step.id === stepId);
  const stepLabel = (place: ObjectivePlacement): string => {
    const step = steps[place.stepIndex];
    const name = step === undefined ? '' : step.name.trim();
    return name === '' ? `étape ${place.stepIndex + 1}` : `étape ${place.stepIndex + 1} · ${name}`;
  };

  const line = (place: ObjectivePlacement, withStep: boolean): PaletteItem => {
    const obligation = place.objective.mandatory ? 'obligatoire' : 'optionnel';
    const duplicate = duplicated(place.objective.name);
    return {
      variable: { kind: 'objective', id: place.objective.id },
      label: place.objective.name,
      meta: withStep || duplicate ? `${stepLabel(place)} · ${obligation}` : obligation,
      duplicate,
    };
  };

  const own = reachable.filter((place) => place.stepIndex === here);
  const before = reachable.filter((place) => place.stepIndex !== here);

  return [
    {
      title: 'objectifs de cette étape',
      items: own
        .filter((place) => matches(place.objective.name))
        .map((place) => line(place, false)),
    },
    {
      title: 'objectifs des étapes précédentes',
      items: before
        .filter((place) => matches(place.objective.name))
        .map((place) => line(place, true)),
    },
    {
      title: 'critères',
      items: criteria
        .filter((card) => card.retiredAt === null && matches(card.name))
        .slice(0, PALETTE_CRITERIA_MAX)
        .map((card) => ({
          variable: { kind: 'criterion', id: card.id } as const,
          label: card.name,
          meta: 'direction du critère',
          duplicate: false,
        })),
    },
  ];
}

/** What Entrée inserts: the first line of the first group that has one. */
export function firstPaletteItem(groups: readonly PaletteGroup[]): PaletteItem | null {
  for (const group of groups) {
    const first = group.items[0];
    if (first !== undefined) return first;
  }
  return null;
}
